import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import StateMap from "@/components/StateMap";
import { ArrowLeft, ArrowUpRight, Building2 } from "lucide-react";

export default function StatePage() {
  const { state } = useParams();
  const [data, setData] = useState({ state, cities: [], total_hotels: 0, state_disabled: false });
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.citiesInState(state),
      api.listHotels({ state, limit: 500 }),
    ])
      .then(([sum, hs]) => {
        setData(sum);
        setHotels(hs);
      })
      .finally(() => setLoading(false));
  }, [state]);

  const upcomingTotal = hotels.filter((h) => h.status === "upcoming").length;
  const totals = hotels.reduce(
    (acc, h) => ({
      rooms: acc.rooms + (Number(h.rooms) || 0),
      banquets: acc.banquets + (Number(h.banquet_halls) || 0),
      restaurants: acc.restaurants + (Number(h.restaurants) || 0),
    }),
    { rooms: 0, banquets: 0, restaurants: 0 }
  );

  // Disabled state → show explicit not-available page
  if (!loading && data.state_disabled) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-label text-xs text-[var(--ink-soft)] hover:text-[var(--ink)] mb-8"
        >
          <ArrowLeft className="w-3 h-3" /> Back to India
        </Link>
        <div data-testid="state-hidden" className="border-2 border-dashed border-[var(--line)] p-8 sm:p-12 text-center">
          <div className="font-label text-xs text-[var(--brand-orange)] mb-3">Not available</div>
          <h1 className="font-display text-3xl sm:text-5xl leading-none mb-4">{state}</h1>
          <p className="text-[var(--ink-soft)] text-sm max-w-md mx-auto">
            This state has been temporarily hidden from public view. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
      <Link
        to="/"
        data-testid="back-to-home"
        className="inline-flex items-center gap-2 font-label text-xs text-[var(--ink-soft)] hover:text-[var(--ink)] mb-6 sm:mb-8"
      >
        <ArrowLeft className="w-3 h-3" /> Back to India
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4 sm:gap-6 mb-8 sm:mb-10">
        <div>
          <div className="font-label text-xs text-[var(--brand-blue)] mb-3 sm:mb-4">State Directory</div>
          <h1 data-testid="state-title" className="font-display text-4xl sm:text-6xl leading-none">
            {state}
          </h1>
        </div>
        <div className="flex gap-4 sm:gap-6">
          <Stat label="Cities" value={data.cities.length} />
          <Stat label="Hotels" value={data.total_hotels} />
          {upcomingTotal > 0 && <Stat label="Upcoming" value={upcomingTotal} accent="text-[var(--brand-orange)]" />}
        </div>
      </div>

      {/* State-level map + stats sidebar */}
      {!loading && hotels.length > 0 && (
        <section className="mb-10 sm:mb-14">
          <div className="mb-4 flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="font-label text-xs text-[var(--ink-soft)] mb-2">Geo Presence</div>
              <h2 className="font-display text-2xl sm:text-3xl">On the ground.</h2>
            </div>
            <p className="text-xs sm:text-sm text-[var(--ink-soft)] max-w-md">
              Each dot represents one or more hotels in a city. Click to jump straight to the city view.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <StateMap stateName={state} hotels={hotels} height={480} />
            </div>
            <aside data-testid="state-stats-panel" className="lg:col-span-1 order-1 lg:order-2 flex flex-col gap-3 sm:gap-4">
              <StatBig
                testId="state-stat-hotels"
                label="Hotels"
                value={hotels.length}
                hint={`across ${data.cities.length} ${data.cities.length === 1 ? "city" : "cities"}`}
                bg="bg-[var(--ink)]"
                fg="text-white"
                accent="text-[var(--brand-yellow)]"
              />
              <StatBig
                testId="state-stat-operational"
                label="Operational"
                value={hotels.filter((h) => h.status !== "upcoming").length}
                hint="Live and welcoming guests"
                bg="bg-[var(--brand-blue)]"
                fg="text-white"
                accent="text-white/70"
              />
              <StatBig
                testId="state-stat-upcoming"
                label="Upcoming"
                value={upcomingTotal}
                hint="Opening soon"
                bg="bg-[var(--brand-orange)]"
                fg="text-white"
                accent="text-white/70"
              />
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <MiniStat testId="state-stat-rooms" label="Rooms" value={totals.rooms} />
                <MiniStat testId="state-stat-banquets" label="Banquets" value={totals.banquets} />
                <MiniStat testId="state-stat-restaurants" label="F&B" value={totals.restaurants} />
              </div>
            </aside>
          </div>
        </section>
      )}

      {loading ? (
        <div className="text-[var(--ink-soft)] font-label text-sm">Loading…</div>
      ) : data.cities.length === 0 ? (
        <EmptyState state={state} />
      ) : (
        <>
          <div className="mb-4">
            <div className="font-label text-xs text-[var(--ink-soft)] mb-2">Cities</div>
            <h2 className="font-display text-2xl sm:text-3xl">Choose a city.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {data.cities.map((c, i) => (
              <Link
                key={c.city}
                to={`/state/${encodeURIComponent(state)}/city/${encodeURIComponent(c.city)}`}
                data-testid={`city-card-${slug(c.city)}`}
                className="group fade-up border border-[var(--ink)] bg-white p-5 sm:p-8 transition-all duration-300 hover:-translate-y-[2px] hover:shadow-[8px_8px_0px_0px_var(--brand-orange)]"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="flex items-start justify-between mb-6 sm:mb-8">
                  <span className="font-label text-[10px] text-[var(--ink-soft)]">City · {String(i + 1).padStart(2, "0")}</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:text-[var(--brand-orange)] transition-colors" />
                </div>
                <h2 className="font-display text-2xl sm:text-4xl leading-tight group-hover:text-[var(--brand-orange)] transition-colors">
                  {c.city}
                </h2>
                <div className="mt-4 sm:mt-6 flex items-center gap-3 flex-wrap text-xs text-[var(--ink-soft)]">
                  <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.hotel_count} {c.hotel_count === 1 ? "hotel" : "hotels"}</span>
                  {c.upcoming > 0 && (
                    <span className="text-[var(--brand-orange)] font-semibold">+{c.upcoming} upcoming</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="font-label text-[10px] text-[var(--ink-soft)]">{label}</div>
      <div className={`font-display text-2xl sm:text-4xl md:text-5xl leading-none ${accent || ""}`}>{value}</div>
    </div>
  );
}

function StatBig({ label, value, hint, bg, fg, accent, testId }) {
  return (
    <div data-testid={testId} className={`${bg} ${fg} p-5 sm:p-6`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-label text-xs opacity-90">{label}</div>
        <div className={`font-label text-[10px] ${accent}`}>Live</div>
      </div>
      <div className="font-display text-5xl sm:text-6xl leading-none mt-3">{value}</div>
      <div className="text-xs mt-3 opacity-80">{hint}</div>
    </div>
  );
}

function MiniStat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="border border-[var(--ink)] p-3 sm:p-4">
      <div className="font-label text-[10px] text-[var(--ink-soft)]">{label}</div>
      <div className="font-display text-xl sm:text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function EmptyState({ state }) {
  return (
    <div data-testid="state-empty" className="border-2 border-dashed border-[var(--line)] p-8 sm:p-10 text-center">
      <div className="font-label text-xs text-[var(--brand-blue)] mb-3">No cities yet in {state}</div>
      <h3 className="font-display text-2xl sm:text-3xl mb-3">Coming soon.</h3>
      <p className="text-[var(--ink-soft)] max-w-md mx-auto text-sm">
        No hotels have been added for {state} yet. Sign in as admin to add the first property here.
      </p>
    </div>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
